import { CheckIcon } from 'lucide-react'
import { type ComponentProps, useState } from 'react'

import { useGetOngoingAssessment } from '@/api/assessments.hook'
import { AssessmentInfo } from '@/gen/inventory/v1/assessment_service_pb'
import { OngoingAssessmentStatus } from '@/gen/inventory/v1/ongoing_assessment_pb'

import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { AntdSteps } from '@/components/lemmala-design-system/steps/steps'

import { ConclusionApprovalView } from './conclusion-approval-view'
import { ConclusionDraftView } from './conclusion-draft-view'
import { CONCLUSION_STEPS, type ConclusionStep } from './consts'
import { CONCLUSION_TAB_LABELS } from './labels'

type StepperItems = NonNullable<ComponentProps<typeof AntdSteps>['items']>

type ConclusionTabProps = {
  assessmentId: AssessmentInfo['id']
  onFinishAssessment?: (hasRejectedAssessment: boolean, isFinalAssessment: boolean) => void
  readonly: boolean
}

export const ConclusionTab = ({
  assessmentId,
  onFinishAssessment,
  readonly,
}: ConclusionTabProps) => {
  const { data: ongoingAssessment, isLoading: isOngoingAssessmentLoading } =
    useGetOngoingAssessment(assessmentId)

  const ongoing = ongoingAssessment?.ongoingAssessment
  const companyId = ongoing?.orgCompanyId ?? ''
  const inAwaitingApproval = ongoing?.status === OngoingAssessmentStatus.AWAITING_APPROVAL

  const [pickedStep, setPickedStep] = useState<ConclusionStep>()
  const selectedStep =
    pickedStep ?? (inAwaitingApproval ? CONCLUSION_STEPS.APPROVAL : CONCLUSION_STEPS.DRAFT)

  if (isOngoingAssessmentLoading) {
    return <Loading />
  }

  const isViewingSubmittedDraft = selectedStep === CONCLUSION_STEPS.DRAFT && inAwaitingApproval

  const stepperItems: StepperItems = [
    {
      title: CONCLUSION_TAB_LABELS.CONCLUSION_STEP,
      icon: isViewingSubmittedDraft ? (
        <span className='flex size-6 items-center justify-center rounded-full bg-main-background-accent'>
          <CheckIcon className='size-3.5' strokeWidth={2.5} />
        </span>
      ) : undefined,
      className: isViewingSubmittedDraft ? '[&_.ant-steps-item-icon]:!bg-transparent' : undefined,
    },
    { title: CONCLUSION_TAB_LABELS.APPROVAL_STEP, disabled: !inAwaitingApproval },
  ]

  return (
    <div className='flex min-h-0 flex-1 gap-6'>
      <div className='shrink-0 bg-main-background-muted py-4'>
        <AntdSteps
          direction='vertical'
          className='py-0'
          size='small'
          current={selectedStep}
          onChange={(step) => setPickedStep(step as ConclusionStep)}
          items={stepperItems}
        />
      </div>
      <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
        {selectedStep === CONCLUSION_STEPS.APPROVAL ? (
          <ConclusionApprovalView
            assessmentId={assessmentId}
            companyId={companyId}
            onFinishAssessment={onFinishAssessment}
            readonly={readonly}
          />
        ) : (
          <ConclusionDraftView
            inAwaitingApproval={inAwaitingApproval}
            assessmentId={assessmentId}
            companyId={companyId}
            readonly={readonly}
            onNext={() => setPickedStep(CONCLUSION_STEPS.APPROVAL)}
          />
        )}
      </div>
    </div>
  )
}
